import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Avatar, Player, PlayerWithToken, Theme } from '@/api/types';

interface PlayState {
  sessionId: string | null;
  pin: string | null;
  player: Player | null;
  /**
   * Opaque token returned by joinSession. Persisted in localStorage
   * (not a cookie — the API gateway is cookie-free) so a phone refresh
   * doesn't kick the player back to the join screen. The token is
   * scoped to a single session; calling reset() clears it.
   */
  sessionToken: string | null;
  nickname: string;
  avatar: string | null;
  theme: Theme;
  setSession: (sessionId: string, pin: string) => void;
  setPlayer: (player: PlayerWithToken) => void;
  setNickname: (n: string) => void;
  setAvatar: (a: string) => void;
  setTheme: (t: Theme) => void;
  reset: () => void;
}

export const usePlayStore = create<PlayState>()(
  persist(
    (set) => ({
      sessionId: null,
      pin: null,
      player: null,
      sessionToken: null,
      nickname: '',
      avatar: null,
      theme: 'default',
      setSession: (sessionId, pin) => set({ sessionId, pin }),
      setPlayer: ({ sessionToken, ...player }) => set({ player, sessionToken }),
      setNickname: (nickname) => set({ nickname }),
      setAvatar: (avatar) => set({ avatar }),
      setTheme: (theme) => set({ theme }),
      reset: () =>
        set({
          sessionId: null,
          pin: null,
          player: null,
          sessionToken: null,
          nickname: '',
          avatar: null,
        }),
    }),
    {
      name: 'massimo-quiz-play',
      partialize: (s) => ({
        sessionId: s.sessionId,
        pin: s.pin,
        player: s.player,
        sessionToken: s.sessionToken,
        nickname: s.nickname,
        avatar: s.avatar,
      }),
    },
  ),
);

export type { Avatar };
